import {
  type ChangeEvent,
  type HTMLAttributes,
  type KeyboardEvent,
  useCallback,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { css, useTheme } from "@emotion/react";
import styled from "@emotion/styled";

import type { FocusEvent } from "react";
import {
  type GridFilterItem,
  type GridRenderHeaderFilterProps,
} from "@mui/x-data-grid-premium";

import {
  autoUpdate,
  FloatingArrow,
  offset,
  shift,
  arrow,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  FloatingPortal,
  flip,
} from "@floating-ui/react";

import { useGridApiContext } from "@mui/x-data-grid-premium";
import { useGridRootProps } from "@mui/x-data-grid-pro/hooks/utils/useGridRootProps";
import { TranslationNamespaceEnum } from "src/lib/i18next/types.ts";
import { BaseInputV2 } from "src/components/BaseInputV2/BaseInputV2.tsx";
import { Flex } from "src/components/Flex.tsx";
import type { ExactTheme } from "src/lib/emotion/theme.ts";
import { TextFilterModeEnum } from "@exonn/js-sdk";
import { logger } from "src/logger";
import { FLOATING_PORTAL_ROOT_ID } from "src/common/constants.ts";
import { useDataGridStore } from "src/components/DataGrid/DataGridStoreProvider.tsx";
import { useDebouncedCallback } from "@exonn/hooks";
import { arrReplaceAt, arrReplaceAtOrInsertInTheEnd } from "@exonn/utils";
import { observer } from "mobx-react-lite";
import { mergeFilterModels } from "src/components/DataGrid/mergeFilterModels.ts";
import { toJS } from "mobx";

const ARROW_HEIGHT = 7;
const GAP = 8;

const OPERATOR_SYMBOL: Record<string, string> = {
  starts_with: "icon-15-strt_wth",
  ends_with: "icon-15-end_wth",
  exact: "icon-15-exact",
  contains: "icon-15-contains",
};

export const DataGridTextHeaderFilterImpl = observer(
  (props: GridRenderHeaderFilterProps) => {
    const { colDef, tabIndex, item, inputRef } = props;

    const store = useDataGridStore();
    const appliedItem = store.appliedFilterModel.items.find(
      (a) => a.field === item.field,
    );

    const [draftValue, setDraftValue] = useState<string | null>(null);

    const { t } = useTranslation();
    const apiRef = useGridApiContext();
    const rootProps = useGridRootProps();
    const theme = useTheme();
    const [hovered, setHovered] = useState<boolean>(false);

    const showHeaderFilterMenu = rootProps.slots.headerFilterMenu !== null;
    const disableColumnHeaderFilter =
      !!rootProps.slotProps?.headerFilterCell?.InputComponentProps?.disabled;
    const [popoverOpen, setPopoverOpen] = useState(false);
    const arrowRef = useRef(null);
    const { refs, floatingStyles, context } = useFloating({
      placement: "top",
      open: popoverOpen,
      onOpenChange: setPopoverOpen,
      whileElementsMounted: autoUpdate,
      middleware: [
        arrow({
          element: arrowRef,
        }),
        offset(ARROW_HEIGHT + GAP),
        shift(),
        flip(),
      ],
    });
    const { getReferenceProps, getFloatingProps } = useInteractions([
      useClick(context),
      useDismiss(context),
    ]);

    const applyFilterChanges = useCallback(
      (updatedItem: GridFilterItem) => {
        if (
          !updatedItem.value &&
          !appliedItem &&
          updatedItem.operator === TextFilterModeEnum.Contains
        ) {
          logger.debug("deleting header filter", {
            item: updatedItem,
          });
          apiRef.current.deleteFilterItem(updatedItem);
        } else {
          logger.debug("upserting header filter", {
            item: updatedItem,
          });
          apiRef.current.upsertFilterItem(updatedItem);
        }
      },
      [apiRef, appliedItem],
    );

    const [applyFilterChangesDebounced, cancelDebounceCallback] =
      useDebouncedCallback(applyFilterChanges, 300);

    const setInputRef = (node: HTMLInputElement | null) => {
      // @ts-ignore eslint-disable-next-line
      inputRef.current = node;
    };

    const handleFocus = () => {
      setDraftValue(item.value ?? appliedItem?.value ?? "");
      apiRef.current.startHeaderFilterEditMode(colDef.field);
    };

    const handleBlur = (event: FocusEvent) => {
      setDraftValue(null);
      apiRef.current.stopHeaderFilterEditMode();
      // Blurring an input element should reset focus state only if `relatedTarget` is not the header filter cell
      if (!event.relatedTarget?.className.includes("columnHeader")) {
        apiRef.current.setState((state) => ({
          ...state,
          focus: {
            cell: null,
            columnHeader: null,
            columnHeaderFilter: null,
            columnGroupHeader: null,
          },
        }));
      }
    };
    const handleOperatorChange = (value: string | undefined) => {
      const nextValue = value ?? TextFilterModeEnum.Contains;

      const nextItem = {
        ...item,
        value: getDisplayedValue(),
        operator: nextValue,
      };
      const prevItems = store.appliedFilterModel.items;

      const isApplied = prevItems.some(
        (appliedItem) => appliedItem.field === item.field,
      );
      if (!isApplied) {
        applyFilterChanges(nextItem);
        setPopoverOpen(false);
        return null;
      }
      const nextItems = arrReplaceAt(
        prevItems,
        (appliedItem) => appliedItem.field === item.field,
        nextItem,
      );

      const nextFilterModel = {
        ...store.appliedFilterModel,
        items: nextItems,
      };
      store.setAppliedFilterModel(nextFilterModel);
      setPopoverOpen(false);
    };

    const getDisplayedOperator = () => {
      if (appliedItem?.operator != null) {
        return appliedItem.operator;
      }

      if (item.operator != null) {
        return item.operator;
      }

      return "";
    };

    const currentOperatorSymbol = OPERATOR_SYMBOL[getDisplayedOperator()];

    const placeholder = t(`header-filters.operator.${getDisplayedOperator()}`, {
      defaultValue: t("header-filters.input-placeholder", {
        ns: TranslationNamespaceEnum.DataGridComponent,
      }),
      ns: TranslationNamespaceEnum.DataGridComponent,
    }) as string;

    const applyValue = (value: string) => {
      const nextValue = processInputValue(value);
      cancelDebounceCallback();
      const nextItem = {
        ...item,
        operator: getDisplayedOperator(),
        value: nextValue,
      };

      const mergedFilterModel = mergeFilterModels(
        apiRef.current.state.filter.filterModel,
        toJS(store.appliedFilterModel),
      );

      const prevItems = mergedFilterModel.items;

      const nextItems = arrReplaceAtOrInsertInTheEnd(
        prevItems,
        (prevItem) => prevItem.field === nextItem.field,
        nextItem,
      );

      const filteredItems = nextItems.filter((item) => !!item.value);

      const nextFilterModel = {
        ...mergedFilterModel,
        items: filteredItems,
      };

      if (rootProps.filterMode === "server") {
        store.setAppliedFilterModel(nextFilterModel);

        // resetting 'not applied filters'
        apiRef.current.setFilterModel({
          items: [],
        });
      } else {
        apiRef.current.setFilterModel(nextFilterModel);
      }

      setTimeout(() => {
        setDraftValue(null);
        setHovered(false);
        (document.activeElement as HTMLElement | null)?.blur();
      });
    };

    const handleKeyUp = (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        applyValue(e.currentTarget.value);
      }
    };

    const handleInputValueChange = (e: ChangeEvent<HTMLInputElement>) => {
      const { value } = e.currentTarget;

      const nextValue = processInputValue(value);

      setDraftValue(nextValue);
      const nextItem = {
        ...item,
        value: nextValue,
      };
      applyFilterChangesDebounced(nextItem);
    };
    const filterMode = rootProps.filterMode;

    const getDisplayedValue = () => {
      if (draftValue != null) {
        return draftValue;
      }
      if (item.value != null) {
        return item.value;
      }
      if (appliedItem?.value != null) {
        return appliedItem.value;
      }
      // empty string fallback is important,
      // item.value can be undefined and if we pass undefined as value prop to input element,
      // then we will enter uncontrolled mode
      return "";
    };
    const inputValue = getDisplayedValue();
    const hasValue = inputValue != null && inputValue != "";

    return (
      <Wrapper disabled={disableColumnHeaderFilter}>
        <BaseInputV2
          disabled={disableColumnHeaderFilter}
          tabIndex={tabIndex}
          ref={setInputRef}
          onPointerEnter={() => {
            setHovered(true);
          }}
          onPointerLeave={() => {
            setHovered(false);
          }}
          value={inputValue}
          onChange={handleInputValueChange}
          placeholder={placeholder}
          onKeyUp={handleKeyUp}
          onFocus={handleFocus}
          onBlur={handleBlur}
          endAdornment={
            <Flex flexShrink={0} gap={"5px"} alignItems={"center"}>
              {hovered && hasValue && filterMode === "server" && (
                <SearchButton
                  onClick={(e) => {
                    e.stopPropagation();
                    applyValue(getDisplayedValue());
                  }}
                />
              )}
              {hovered && hasValue && filterMode === "server" && (
                <ClearButton
                  onClick={(e) => {
                    e.stopPropagation();
                    applyValue("");
                  }}
                />
              )}

              {showHeaderFilterMenu && (
                <GearContainer>
                  {currentOperatorSymbol && (
                    <CurrentOperator
                      {...getReferenceProps()}
                      ref={refs.setReference}
                    >
                      <i className={currentOperatorSymbol} />
                    </CurrentOperator>
                  )}
                </GearContainer>
              )}
            </Flex>
          }
        />

        {popoverOpen && (
          <FloatingPortal id={FLOATING_PORTAL_ROOT_ID}>
            <Popover
              {...getFloatingProps()}
              ref={refs.setFloating}
              style={floatingStyles}
            >
              <div
                style={{
                  borderRadius: "6px",
                }}
              >
                {colDef.filterOperators?.map((operator) => {
                  return (
                    <div
                      css={filterItem({
                        isActive: getDisplayedOperator() === operator.value,
                      })}
                      key={operator.value}
                      onClick={() => handleOperatorChange(operator.value)}
                    >
                      <div>
                        <Flex>{operator.headerLabel}</Flex>
                      </div>
                    </div>
                  );
                })}
              </div>
              <FloatingArrow
                height={ARROW_HEIGHT}
                strokeWidth={1}
                stroke={theme.colors.border.muted}
                fill="white"
                ref={arrowRef}
                context={context}
              />
            </Popover>
          </FloatingPortal>
        )}
      </Wrapper>
    );
  },
);

const Popover = styled.div`
  position: relative;
  min-width: 202px;
  max-width: 300px;
  background-color: white;
  box-shadow: ${({ theme }) => theme.shadowV2.default};
  border-radius: 6px;
  z-index: ${({ theme }) => theme.zIndices.popovers};
`;

const ClearButton = (props: HTMLAttributes<HTMLButtonElement>) => {
  return (
    <button
      {...props}
      css={(theme: ExactTheme) => css`
        display: flex;
        flex-shrink: 0;
        background-color: transparent;
        color: ${theme.colorsV2.grey[500]};
        align-items: center;
        border: none;
        padding: 0;
        cursor: pointer;

        :hover {
          color: ${theme.colorsV2.red[500]};
        }
      `}
    >
      <i className="icon-15-clear_x" />
    </button>
  );
};

const SearchButton = (props: HTMLAttributes<HTMLButtonElement>) => {
  return (
    <button
      {...props}
      css={(theme: ExactTheme) => css`
        display: flex;
        flex-shrink: 0;
        background-color: transparent;
        border: none;
        padding: 0;
        cursor: pointer;
        :hover {
          color: ${theme.colorsV2.blue[500]};
        }
      `}
    >
      <i className={"icon-15-search"} />
    </button>
  );
};

const CurrentOperator = styled.span`
  align-items: center;
  /* border: 1px solid #D1D7DE; */
  /* color: #D1D7DE; */

  margin-right: 7px;
  justify-content: center;
  display: flex;
  height: 25px;
  background: white;
  border-radius: 100%;
  font-size: 15px;
`;

const Wrapper = styled.div<{ disabled: boolean | undefined }>`
  position: relative;
  overflow: hidden;
  width: 100%;
  height: 100%;
  margin-top: -1px;
  // 1px padding is needed to not cut off input outline
  padding: 1px 10px 10px;

  ${({ disabled }) =>
    disabled && {
      opacity: 0.4,
      pointerEvents: "none",
    }}
`;

const GearContainer = styled.div`
  position: relative;
  cursor: pointer;
`;

const filterItem =
  ({ isActive }: { isActive: boolean }) =>
  (theme: ExactTheme) => css`
    padding-right: 10px;
    padding-left: 10px;
    height: 35px;
    display: flex;
    align-items: center;
    border-bottom: 1px solid ${theme.colorsV2.grey[100]};
    cursor: pointer;
    background: ${isActive ? theme.colorsV2.grey[100] : ""};
    &:last-of-type {
      border: unset;
      border-bottom-left-radius: 6px;
      border-bottom-right-radius: 6px;
    }
    &:first-of-type {
      border-top-left-radius: 6px;
      border-top-right-radius: 6px;
    }
    &:hover {
      background-color: ${theme.colorsV2.grey[50]};
    }
  `;

const processInputValue = (value: string): string => {
  return value;
};
